import moment from "moment-timezone";
import { Check as CheckIcon, Trash2 as TrashIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContractTypeOptions, CommodityOptions } from "@/lib/api/otc";
import { formatNumber } from "@/section/otc/common";
import { safeParseDecimal } from "@/lib/api/common";
import { CommonDestinationTimezone, createDateLabel } from "@/lib/utils";
import { SelectOptionItem } from "@/lib/models/ui";
import { dateWithFormatSort } from "@/components/ui/DataTable/sorting";
import type { SortingFn } from "@tanstack/react-table";
import { DataTableColumn } from "@/components/ui/DataTable";

/**
 * Sorting function that only cares about null/falsy vs truthy.
 * Nulls/falsy sort first (asc), truthy values are treated as equal (returns 0)
 * so the next sort column in the chain takes over.
 */
const nullsFirstSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  const aEmpty = !a;
  const bEmpty = !b;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;
  return 0;
};

export function getOtcContractColumns(options?: {
  showUnstageButton?: boolean;
  showDeleteButton?: boolean;
  onUnstage?: (id: string) => void;
  onDelete?: (id: string) => void;
}): any[] {
  const {
    showUnstageButton = false,
    showDeleteButton = false,
    onUnstage,
    onDelete,
  } = options || {};

  const columns: DataTableColumn<any>[] = [];

  // Action columns (only for staged contracts)
  if (showUnstageButton && onUnstage) {
    columns.push({
      id: "unstage_button",
      header: "Approve",
      cell: ({ row }: any) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onUnstage(row.original.id)}
        >
          <CheckIcon className="h-4 w-4" />
        </Button>
      ),
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  if (showDeleteButton && onDelete) {
    columns.push({
      id: "delete_button",
      header: "Delete",
      cell: ({ row }: any) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(row.original.id)}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      ),
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  // Base columns
  columns.push(
    {
      accessorKey: "created_at",
      header: "Created At",
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      accessorFn: (row: any) =>
        row.created_at
          ? row.created_at
          : "",
      cell: (info) => info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("hh:mm A MM/DD/YYYY") : ""
    },
    {
      accessorKey: "CreatedByUser.name",
      header: "Created By",
      accessorFn: (row: any) => row.CreatedByUser?.name
    },
    {
      accessorKey: "UpdatedByUser.name",
      header: "Updated By",
      accessorFn: (row: any) => row.UpdatedByUser?.name
    },
    {
      accessorKey: "date",
      header: "Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      size: 130,
      minSize: 90,
      accessorFn: (row: any) =>
        moment.tz(row.date, CommonDestinationTimezone).format("MM/DD/YYYY"),
    },
    {
      header: "Brokerage OTC",
      accessorFn: (row: any) => row.AccountNoA?.account_id || "",
    },
    {
      header: "Brokerage HTA",
      accessorFn: (row: any) => row.AccountNoB?.account_id || "",
    },
    {
      accessorKey: "contract_no",
      header: "Contract #",
      accessorFn: (row: any) => row.contract_no,
    },
    {
      accessorKey: "contract_reference",
      header: "Contract Reference",
      accessorFn: (row: any) => row.contract_reference,
    },
    {
      id: "customer_id",
      header: "Customer",
      accessorFn: (row: any) => row.Customer?.long_name || "",
    },
    {
      id: "trader_id",
      header: "Trader",
      accessorFn: (row: any) => row.Trader?.name || "",
    },
    {
      id: "contract_type",
      header: "Contract Type",
      accessorFn: (row: any) => {
        return ContractTypeOptions.find(item => item.value === row.contract_type)?.label || row.contract_type;
      },
    },
    {
      id: "quantity",
      header: "Quantity",
      accessorFn: (row: any) => formatNumber(row.quantity),
    },
    {
      id: "commodity_label",
      header: "Commodity",
      accessorFn: (row: any) => {
        const commodity_type = row.commodity;
        return (
          CommodityOptions.find((option: SelectOptionItem<string>) => {
            return (
              option.value?.toLocaleLowerCase() ===
              commodity_type?.toLocaleLowerCase()
            );
          })?.label || commodity_type
        );
      },
    },
    {
      id: "crop_year",
      accessorKey: "crop_year",
      header: "Crop Year",
    },
    {
      id: "option_month",
      accessorKey: "option_month",
      header: "OTC Opt. Month",
    },
    {
      id: "option_month_etc_one",
      accessorKey: "option_month_etc_one",
      header: "HTA DU Opt. Month",
    },
    {
      accessorKey: "notes",
      header: "Notes",
      enableColumnFilter: false,
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.start_date),
    },
    {
      accessorKey: "end_date",
      header: "Expiration Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.end_date),
    },
    {
      accessorKey: "shipment_start_date",
      header: "Shipment Start Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.shipment_start_date),
    },
    {
      accessorKey: "shipment_end_date",
      header: "Shipment End Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.shipment_end_date),
    },
    {
      accessorKey: "weeks",
      header: "Weeks",
      cell: ({ row }: any) => row.original.weeks,
    },
    {
      accessorKey: "period_qty",
      header: "Period Qty",
      cell: ({ row }: any) => row.original.period_qty,
    },
    {
      accessorKey: "knockout",
      header: "Knockout",
      cell: ({ row }: any) => row.original.knockout,
    },
    {
      id: "step_one",
      header: "Step 1",
      cell: ({ row }: any) => row.original.step_one,
    },
    {
      id: "step_two",
      header: "Step 2",
      cell: ({ row }: any) => row.original.step_two,
    },
    {
      id: "step_three",
      header: "Step 3",
      cell: ({ row }: any) => row.original.step_three,
    },
    {
      id: "double_up",
      header: "Double Up",
      cell: ({ row }: any) => row.original.double_up,
    },
    {
      id: "option_premium",
      header: "Option Premium",
      cell: ({ row }: any) => row.original.option_premium,
    },
    {
      id: "contract_fee",
      header: "Initial Premium + Fee",
      cell: ({ row }: any) => row.original.contract_fee,
    },
    {
      id: "margin",
      header: "Lighthouse Fee",
      cell: ({ row }: any) => formatNumber(row.original.margin),
    },
    {
      id: "margin_amount",
      header: "Margin Amount",
      cell: ({ row }: any) => formatNumber(row.original.margin_amount),
    },
    {
      accessorKey: "trade_reference",
      header: "Trade Reference",
      cell: ({ row }: any) => row.original.trade_reference,
    },
    {
      accessorKey: "futures_price",
      header: "Futures Price",
      cell: ({ row }: any) => formatNumber(row.original.futures_price),
    },
    {
      accessorKey: "otc_futures",
      header: "OTC Futures",
      cell: ({ row }: any) => formatNumber(row.original.otc_futures),
    },
    {
      accessorKey: "rolled_option_month",
      header: "Futures Rolled Month",
      cell: ({ row }: any) => row.original.rolled_option_month,
    },
    {
      accessorKey: "rolled_basis_month",
      header: "Basis Rolled Month",
      cell: ({ row }: any) => row.original.rolled_basis_month,
    },
    {
      accessorKey: "carry",
      header: "Futures Carry",
      cell: ({ row }: any) =>
        safeParseDecimal({})(row.original.carry)?.toFixed(2),
    },
    {
      accessorKey: "basis_carry",
      header: "Basis Carry",
      cell: ({ row }: any) =>
        safeParseDecimal({})(row.original.basis_carry)?.toFixed(2),
    },
    {
      id: "adj_option_premium",
      header: "Adj Option Premium",
      cell: ({ row }: any) => row.original.adj_option_premium,
    },
    {
      id: "adj_contract_fee",
      header: "Adj Initial Premium + Fee",
      cell: ({ row }: any) => row.original.adj_contract_fee,
    },
    {
      id: "adj_margin",
      header: "Adj Lighthouse Fee",
      cell: ({ row }: any) => formatNumber(row.original.adj_margin),
    },
    {
      id: "adj_margin_amount",
      header: "Adj Margin Amount",
      cell: ({ row }: any) => formatNumber(row.original.adj_margin_amount),
    },
    {
      id: "last_adjusted_date",
      header: "Last Adj Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.last_adjusted_date),
    }
  );

  // Hidden phantom column for nulls-first sorting on contract_no.
  columns.push({
    id: "contract_no_nulls",
    accessorFn: (row: any) => row.contract_no,
    header: "contract_no_nulls",
    sortingFn: nullsFirstSort,
    enableHiding: false,
    enableColumnFilter: false,
  });

  return columns;
}

